import React from "react";
import { cva } from "class-variance-authority";
import { RnCheckmark, RnChevronDown, RnChevronRight } from "@remi/icons/next";
import { isTruthy } from "@remi/ts-utils";
import type { KeyList } from "@remi/types/keyboard";

import { useMedia } from "../../hooks";
import { cn } from "../../lib/utils";
import { RemiShortcut } from "../RemiShortcut";
import { DropdownCommandItem } from "./DropdownCommand";

const selectItemVariants = cva("!rounded-md bg-background-primary", {
  variants: {
    variant: {
      primary:
        "text-text-secondary hover:bg-background-secondaryHover sm:data-[selected=true]:bg-background-secondaryHover",
      destructive:
        "text-text-destructive hover:bg-background-destructiveLight sm:data-[selected=true]:bg-background-destructiveLight",
    },
    appearance: {
      standard: "flex w-full items-center justify-between sm:gap-2",
      custom: "w-full",
    },
    size: {
      sm: "h-7 text-xs sm:h-6 sm:text-xxs",
      md: "h-11 text-sm md:h-8 md:text-xs",
      lg: "h-11 text-sm",
      auto: "min-h-11 text-sm md:min-h-8 md:text-xs",
    },
  },
  defaultVariants: {
    variant: "primary",
    appearance: "custom",
    size: "md",
  },
});

interface SelectItemProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "primary" | "destructive";
  appearance?: "standard" | "custom";
  size?: "sm" | "md" | "lg" | "auto";
  value?: string;
  onSelect: () => void;
  icon?: React.ReactNode;
  expandable?: boolean;
  shortcut?: KeyList;
  selected?: boolean;
  open?: boolean;
  layer?: number;
  disabled?: boolean;
  hidden?: boolean;
  hint?: React.ReactNode;
  selectedIcon?: React.ReactNode;
}

const SelectItem = ({
  ref,
  variant = "primary",
  appearance = "custom",
  size = "md",
  expandable = false,
  selected = false,
  open = false,
  icon,
  shortcut,
  onSelect,
  value,
  children,
  className,
  layer = 0,
  disabled,
  hidden = false,
  hint,
  selectedIcon,
  id,
  ...props
}: SelectItemProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const { isMobile } = useMedia();
  selectedIcon = selectedIcon ?? <RnCheckmark size={size === "sm" ? "xs" : "sm"} />;

  if (hidden) {
    return null;
  }

  return (
    <DropdownCommandItem
      ref={ref}
      id={id}
      disabled={disabled}
      className={cn(
        selectItemVariants({ variant, appearance, size }),
        className,
        "cursor-pointer",
        disabled === true && "cursor-default hover:bg-background-primary sm:data-[selected=true]:bg-background-primary",
      )}
      value={value}
      onSelect={onSelect}
      {...props}
    >
      {appearance === "custom" && children}
      {appearance === "standard" && (
        <>
          <div className="flex min-w-0 flex-1 items-center gap-1">
            {layer !== 0 && isMobile && <span style={{ width: `${layer / 2}rem` }} />}
            {isTruthy(icon) && <span className="mr-1 min-w-[1rem]">{icon}</span>}
            <span className="flex min-w-0 flex-1">{children}</span>
          </div>
          <span
            className={cn(
              "flex items-center overflow-hidden transition-all duration-150",
              expandable || (shortcut && !hint && !selected) || hint || selected ? "min-w-[1rem]" : "w-0",
            )}
          >
            {expandable && isMobile && (
              <RnChevronDown size="sm" className={cn("transition-all", open ? "rotate-0" : "-rotate-90")} />
            )}
            {expandable && !isMobile && <RnChevronRight size="xs" />}
            {shortcut && !hint && !selected && <RemiShortcut shortcut={shortcut} />}
            {hint && <span className="inline-flex items-center text-text-tertiary">{hint}</span>}
            {selected && selectedIcon}
          </span>
        </>
      )}
    </DropdownCommandItem>
  );
};
SelectItem.displayName = "SelectItem";

export { SelectItem };
